import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { getAlerts, createAlert, deleteAlert, getNotifications, markAllNotificationsRead, deleteNotification } from '../api';

const WA_COUNTIES = [
  'Adams','Asotin','Benton','Chelan','Clallam','Clark','Columbia','Cowlitz','Douglas',
  'Ferry','Franklin','Garfield','Grant','Grays Harbor','Island','Jefferson','King',
  'Kitsap','Kittitas','Klickitat','Lewis','Lincoln','Mason','Okanogan','Pacific',
  'Pend Oreille','Pierce','San Juan','Skagit','Skamania','Snohomish','Spokane',
  'Stevens','Thurston','Wahkiakum','Walla Walla','Whatcom','Whitman','Yakima',
];

const CATEGORY_LABELS = {
  any:          'Any inpatient bed',
  mh_inpatient: 'MH inpatient',
  sud_inpatient:'SUD inpatient',
  detox:        'Detox / withdrawal management',
};

const POPULATION_LABELS = {
  any:   'Any',
  adult: 'Adult',
  youth: 'Youth',
};

const GENDER_LABELS = {
  any:    'Any',
  male:   'Male',
  female: 'Female',
};

const BLANK_FORM = {
  bed_category:    'any',
  county:          '',
  population:      'any',
  gender:          'any',
  needs_mat:       false,
  needs_psych_meds: false,
  needs_ppw:       false,
  notes:           '',
};

export default function Alerts() {
  const { hasFullAccess } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('alerts'); // 'alerts' | 'notifications'

  useEffect(() => {
    getAlerts().then(setAlerts).catch(() => {});
    getNotifications().then(setNotifications).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const created = await createAlert({
        bed_category:    form.bed_category,
        county:          form.county || null,
        population:      form.population,
        gender:          form.gender,
        needs_mat:       form.needs_mat,
        needs_psych_meds: form.needs_psych_meds,
        needs_ppw:       form.needs_ppw,
        notes:           form.notes || null,
      });
      setAlerts((a) => [created, ...a]);
      setForm(BLANK_FORM);
    } catch {
      setError('Failed to create alert. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    await deleteAlert(id);
    setAlerts((a) => a.filter((x) => x.id !== id));
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    setNotifications((ns) => ns.map((n) => ({ ...n, is_read: true })));
  };

  const handleDeleteNotification = async (id) => {
    await deleteNotification(id).catch(() => {});
    setNotifications((ns) => ns.filter((n) => n.id !== id));
  };

  const unread = notifications.filter((n) => !n.is_read).length;

  // Bed alerts are gated to Individual and Org subscribers
  if (!hasFullAccess) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        <Helmet><title>Bed Alerts | WA BHA Treatment Finder</title></Helmet>
        <Header subtitle="Bed Alerts" />
        <div className="flex-1 max-w-2xl mx-auto w-full px-4 py-12 flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Bed Alerts require an Individual or Org subscription
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-6">
            Get notified the moment a facility opens a bed that matches your criteria.
            Upgrade to set up personalized bed alerts.
          </p>
          <Link
            to="/subscribe"
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
          >
            View plans
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <Helmet><title>Bed Alerts | WA BHA Treatment Finder</title></Helmet>
      <Header subtitle="Bed Alerts" />

      <div className="flex-1 max-w-2xl mx-auto w-full px-4 py-8">

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
          {['alerts', 'notifications'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                tab === t
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {t === 'notifications' && unread > 0 ? `Notifications (${unread})` : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {tab === 'alerts' && (
          <>
            {/* Create form */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">New bed alert</h2>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Bed category */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Bed type</label>
                    <select
                      value={form.bed_category}
                      onChange={(e) => setForm((f) => ({ ...f, bed_category: e.target.value }))}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  </div>

                  {/* Population */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Age group</label>
                    <select
                      value={form.population}
                      onChange={(e) => setForm((f) => ({ ...f, population: e.target.value }))}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      {Object.entries(POPULATION_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  </div>

                  {/* Gender */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Gender</label>
                    <select
                      value={form.gender}
                      onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      {Object.entries(GENDER_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  </div>

                  {/* County */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">County <span className="text-gray-400">(optional)</span></label>
                    <select
                      value={form.county}
                      onChange={(e) => setForm((f) => ({ ...f, county: e.target.value }))}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="">Any county</option>
                      {WA_COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                {/* Service checkboxes */}
                <div>
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Required services <span className="text-gray-400 font-normal">(only notify if facility offers these)</span></p>
                  <div className="flex flex-wrap gap-4">
                    {[
                      { key: 'needs_mat',       label: 'MAT services' },
                      { key: 'needs_psych_meds', label: 'Psychiatric medication management' },
                      { key: 'needs_ppw',       label: 'Pregnant/Parenting Women (PPW)' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={form[key]}
                          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-400"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notes <span className="text-gray-400">(optional reminder for yourself)</span></label>
                  <input
                    type="text"
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    placeholder="e.g. for client John"
                    maxLength={200}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>

                {error && <p className="text-xs text-red-600">{error}</p>}

                <button
                  type="submit"
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  {saving ? 'Saving…' : 'Create alert'}
                </button>
              </form>
            </div>

            {/* Alert list */}
            <div className="space-y-3">
              {alerts.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">No alerts yet. Create one above.</p>
              )}
              {alerts.map((alert) => {
                const tags = [];
                if (alert.gender && alert.gender !== 'any') tags.push(GENDER_LABELS[alert.gender]);
                if (alert.needs_mat)        tags.push('MAT');
                if (alert.needs_psych_meds) tags.push('Psych meds');
                if (alert.needs_ppw)        tags.push('PPW');

                return (
                  <div key={alert.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {CATEGORY_LABELS[alert.bed_category]} · {POPULATION_LABELS[alert.population]}
                        {alert.county ? ` · ${alert.county} County` : ' · Any county'}
                      </p>
                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {tags.map((tag) => (
                            <span key={tag} className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[11px] font-medium px-2 py-0.5 rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      {alert.notes && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Note: {alert.notes}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDelete(alert.id)}
                      className="text-xs text-red-500 hover:text-red-700 flex-shrink-0 mt-0.5"
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tab === 'notifications' && (
          <div>
            {unread > 0 && (
              <div className="flex justify-end mb-3">
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Mark all as read
                </button>
              </div>
            )}
            <div className="space-y-3">
              {notifications.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">No notifications yet.</p>
              )}
              {notifications.map((n) => (
                <div
                  key={n.id}
                  className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${
                    n.is_read
                      ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 dark:text-gray-200">{n.message}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {new Date(n.created_at).toLocaleDateString(undefined, {
                        month: 'long', day: 'numeric', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteNotification(n.id)}
                    title="Delete"
                    className="flex-shrink-0 text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400 transition-colors mt-0.5"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
